import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';

import AddLeadSource from 'modules/lead/AddLead/components/CaseInformation/AddLeadSource';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import { updateLeadDetails } from 'services/lead/leadServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useToggle } from 'hooks/useToggle';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { queryClient } from 'config/queryClient';
import { queryKeys } from 'modules/LeadsV2/config/queryClient';
import { gratitudeOptions } from 'constants/dropdownOptions';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import constants from 'constants/index';
import AsyncPaginate from 'components/AsyncPaginate/AsyncPaginate';
import ReferredOutDialog from '../ReferredOutDialog/ReferredOutDialog';
import { useReferredToContact } from 'hooks/useReferredToContact';
import { getGlobalDropdown } from 'services/caseLeadDropdownService';
import { contactDetails, updateContactDetails } from 'services/contact';
import 'components/UI/shared/AsyncSelect.scss';

export default function Summary(props) {
  const { leadDetails, loadLeadSources, leadSourceList, onCaseClose, loadLeadDetails } = props;
  const leadStageValue = leadDetails?._lead_stage_name_;
  const leadStageId = leadDetails?.lead_stage;
  const leadSourceValue = leadDetails?.lead_details?._lead_source_;
  const leadSourceId = leadDetails?.lead_details?.source;
  const referredFromId = leadDetails?.referred_from;
  const referredFromValue = `${leadDetails?._ref_from_title_ || ''} ${leadDetails?._ref_from_first_name_ || ''} ${
    leadDetails?._ref_from_last_name_ || ''
  }`.trim();

  const [sourceToggle, setSourceToggle] = useToggle();
  const [apiLoading, setApiLoading] = useToggle();
  const [onSourceReferral, setOnSourceReferral] = useToggle(false);
  const [referralToggle, setReferralToggle] = useToggle();
  const { addToast } = useToast();
  const userContext = useUserDetailsContext();
  const { contactList, searchAsync, loading: contactLoading } = useReferredToContact(false, '', false, 15, true);

  const { control, reset, handleSubmit, setValue, watch } = useForm();

  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const [leadStagesOptions, setLeadStagesOptions] = useState([]);
  const [leadStageSearchQuery, setLeadStageSearchQuery] = useState('');
  const [referredFromOptions, setReferredFromOptions] = useState([]);
  const [referredFromSearchQuery, setReferredFromSearchQuery] = useState('');
  const [newlyCreatedReferral, setNewlyCreatedReferral] = useState(null);
  const [leadSourceSearchQuery, setLeadSourceSearchQuery] = useState('');

  useEffect(() => {
    searchAsync({ query: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails]);

  useEffect(() => {
    if (leadDetails?.referred_from) {
      contactDetails('', '', leadDetails.referred_from)
        .then((response) => {
          const contactData = response?.data;
          if (contactData) {
            setNewlyCreatedReferral({
              label:
                contactData.contact_type === 'person'
                  ? `${contactData.title || ''} ${contactData.first_name || ''} ${contactData.last_name || ''}`.trim()
                  : contactData.title || contactData.business_name || 'Business Contact',
              value: contactData.contact_id,
              contact_id: contactData.contact_id,
              ...contactData,
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching referred from contact:', error);
        });
    } else {
      setNewlyCreatedReferral(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails?.referred_from]);

  useEffect(() => {
    if (leadDetails) {
      reset({
        ...leadDetails,
        last_login: leadDetails?.main_client[0]?.last_login
          ? convertDateFromApiToUserTimezone(
              leadDetails?.main_client[0]?.last_login,
              userContext?.userDetails?.timezone,
              constants.month_date_year_time_12_format
            )
          : '--',
      });
    }
  }, [leadDetails, userContext?.userDetails?.timezone]);

  const leadSource = watch('lead_details.source');
  const gratitudeValue = watch('lead_details.gratitude');
  useEffect(() => {
    let lead_source_id = leadSourceList?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;

    if (!lead_source_id) {
      lead_source_id = leadSourceOptions?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;
    }

    if (leadSource === lead_source_id) {
      setOnSourceReferral(true);
    } else {
      setOnSourceReferral(false);
    }
  }, [leadSource, leadSourceList]);

  const sourceAddOnSuccess = (data) => {
    if (data) {
      setValue('lead_details.source', leadSourceId);
    }
    loadLeadSources();
    setSourceToggle(false);
  };

  const referralAddOnSuccess = (contactId, contactData) => {
    if (contactId) {
      setValue('referred_from', contactId);
      if (contactData) {
        setNewlyCreatedReferral({
          label:
            contactData.contact_type === 'person'
              ? `${contactData.title || ''} ${contactData.first_name || ''} ${contactData.last_name || ''}`.trim()
              : contactData.title || contactData.business_name || 'Business Contact',
          value: contactData.contact_id,
          contact_id: contactData.contact_id,
          ...contactData,
        });
      }
    }
    setReferralToggle(false);
  };

  const loadReferralOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit, pagination_token, pagination_direction } = additional;
    const searchQuery = inputValue || '';

    if (!pagination_token) {
      setReferredFromSearchQuery(searchQuery);
    }

    const response = await searchAsync({ query: searchQuery }, pagination_token, pagination_direction, pageLimit);

    const formattedOptions = (response?.contacts || []).map((contact) => ({
      label:
        contact.contact_type === 'person'
          ? `${contact.title || ''} ${contact.first_name} ${contact.last_name}`.trim()
          : contact.title || contact.business_name || 'Business Contact',
      value: contact.contact_id,
      contact_id: contact.contact_id,
      ...contact,
    }));

    if (!pagination_token) {
      setReferredFromOptions(formattedOptions);
    } else {
      setReferredFromOptions((prevOptions) => [...prevOptions, ...formattedOptions]);
    }

    return {
      options: formattedOptions,
      hasMore: response?.next_token ? true : false,
      nextToken: response?.next_token || '',
      transitionToken: response?.transition_token || '',
      additional: {
        pageLimit: pageLimit,
        pagination_token: response?.next_token || '',
        pagination_direction: pagination_direction,
      },
    };
  };

  const updateLead = (formData) => {
    const { case_description, lead_details, lead_stage, lead_status, sk, referred_from } = formData || {};
    let referralSourceId = leadSourceList?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;

    if (!referralSourceId) {
      referralSourceId = leadSourceOptions?.find((item) => item?.source?.toLowerCase() === 'referral')?.lead_source_id;
    }

    const updatedLeadDetails = { ...lead_details };
    if (updatedLeadDetails.source !== referralSourceId) {
      updatedLeadDetails.gratitude = null;
      updatedLeadDetails.gratitude_item = '';
    }
    setApiLoading(true);
    updateLeadDetails(sk, {
      case_description,
      lead_details: updatedLeadDetails,
      lead_stage,
      lead_status,
      referred_from: updatedLeadDetails.source !== referralSourceId ? null : referred_from,
    })
      .then(() => {
        setApiLoading(false);
        loadLeadDetails();
        queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_UPDATE_SUCCESS);
      })
      .catch(() => {
        setApiLoading(false);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_UPDATE_FAILURE);
      });
  };

  const loadLeadSourceOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;

    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;

    const searchQuery = inputValue || '';

    if (!page) {
      setLeadSourceSearchQuery(searchQuery);
    }

    const options = await getGlobalDropdown('', 'lead_source', inputValue, leadDetails?.case_id, true, page, pageLimit);

    if (!page) {
      setLeadSourceOptions(options?.data?.data || []);
    } else {
      setLeadSourceOptions((leadSourceOptions) => [...leadSourceOptions, ...(options?.data?.data || [])]);
    }

    return {
      options: options?.data?.data || [],
      hasMore: options?.data?.has_next,
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
    };
  };

  const loadLeadStageOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;

    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;

    const searchQuery = inputValue || '';

    if (!page) {
      setLeadStageSearchQuery(searchQuery);
    }

    const data = {
      practice_area_id: leadDetails?.case_description?.case_practice_area ? [leadDetails?.case_description?.case_practice_area] : [],
      show_unassigned_pa_column: true,
    };

    const options = await getGlobalDropdown('', 'lead_stage', inputValue, '', true, page, pageLimit, '', 'ACTIVE', data);

    if (!page) {
      setLeadStagesOptions(options?.data?.data || []);
    } else {
      setLeadStagesOptions((prev) => [...prev, ...(options?.data?.data || [])]);
    }

    return {
      options: options?.data?.data || [],
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
      hasMore: options?.data?.has_next,
    };
  };

  useEffect(() => {
    if (referredFromOptions?.length === 0) {
      setReferredFromOptions(contactList);
    }
  }, [contactList]);

  const onHideReferral = (e) => {
    setReferralToggle(false);
    if (e === 'closed') {
      loadLeadDetails();
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
    }
  };

  return (
    <React.Fragment>
      <h5 className="caption-bold">SUMMARY</h5>
      <div className="card-bg lead-data-box p-3">
        <div className="row">
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Last Login</div>
              <div className="col-lg-8 col-12">
                <Controller
                  name="last_login"
                  control={control}
                  render={({ field }) => <InputText disabled id={field.name} {...field} className="w-100 input-shadow" />}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Lead Stage</div>
              <div className="col-lg-8 col-12">
                <Controller
                  name="lead_stage"
                  control={control}
                  render={({ field }) => {
                    let selectedOption = leadStagesOptions?.find((item) => item?.sk === field?.value) || null;
                    if (!selectedOption) {
                      selectedOption = (leadStageValue && leadStageId && { label: leadStageValue, value: leadStageId }) || null;
                    }
                    return (
                      <AsyncPaginate
                        name={field.name}
                        value={selectedOption}
                        onChange={(selectedOption) => {
                          typeof onCaseClose === 'function' &&
                            selectedOption?.lead_stage_name?.toLowerCase() === 'closed' &&
                            onCaseClose(selectedOption?.sk);
                          field.onChange(selectedOption?.sk);
                        }}
                        loadOptions={loadLeadStageOptions}
                        getOptionLabel={(option) => option?.label || option?.lead_stage_name}
                        getOptionValue={(option) => option?.value || option?.sk}
                        additional={{
                          pageLimit: 15,
                          page: 0,
                        }}
                        cacheUniqs={[leadStageSearchQuery, leadDetails?.case_description?.case_practice_area]}
                        debounceTimeout={300}
                        placeholder="Select"
                        className="outline-dropdown w-100"
                        styles={userContext?.userDetails?.appearance_preference}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Lead Priority</div>
              <div className="col-lg-8 col-12">
                <Controller
                  name="lead_details.priority"
                  control={control}
                  render={({ field }) => (
                    <PriorityDropdown
                      placeholder="Select"
                      id={field.name}
                      onChange={(e) => field.onChange(e.value)}
                      value={field.value}
                      className="outline-dropdown w-100"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Potential Case Value</div>
              <div className="col-12 col-lg-8">
                <Controller
                  name="case_description.potential_case_value"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      className="w-100 input-shadow"
                      inputId="potential-case-value-input"
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      min={0}
                      maxFractionDigits={2}
                      id={field.name}
                      onKeyDown={(e) => {
                        if (!/^\d$/.test(e.key)) return;
                        const input = e.target;
                        if (input.selectionStart !== input.selectionEnd) return;
                        const raw = input.value.replace(/[^0-9.]/g, '');
                        const intDigits = raw.split('.')[0]?.length || 0;
                        const dotPos = input.value.indexOf('.');
                        const cursorInDecimal = dotPos !== -1 && input.selectionStart > dotPos;
                        if (!cursorInDecimal && intDigits >= 10) e.preventDefault();
                      }}
                      onChange={(e) => {
                        if (e.value != null && e.value > 9999999999.99) return;
                        field.onChange(e.value);
                      }}
                      value={field.value}
                      inputProps={{
                        autoComplete: 'new-password',
                        'data-lpignore': 'true',
                        'data-form-type': 'other',
                        name: 'potential_case_value_' + Math.random().toString(36),
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center py-2 flex-wrap">
              <div className="col-lg-4 col-12 p-medium">Lead Source</div>
              <div className="col-lg-8 col-12">
                {sourceToggle ? (
                  <AddLeadSource onSuccess={sourceAddOnSuccess} onDismiss={setSourceToggle} />
                ) : (
                  <div className="d-flex align-items-center">
                    <Controller
                      name="lead_details.source"
                      control={control}
                      render={({ field }) => {
                        // <Dropdown
                        //   id={field.name}
                        //   onChange={(e) => {
                        //     field.onChange(e.value);
                        //   }}
                        //   value={field.value}
                        //   optionLabel="source"
                        //   optionValue="lead_source_id"
                        //   options={leadSourceList}
                        //   placeholder="Select"
                        //   className="input-shadow w-100"
                        //   filter
                        // />
                        let selectedOption = leadSourceOptions?.find((item) => item?.lead_source_id === field?.value) || null;
                        if (!selectedOption) {
                          selectedOption = (leadSourceValue && leadSourceId && { label: leadSourceValue, value: leadSourceId }) || null;
                        }
                        return (
                          <AsyncPaginate
                            name={field.name}
                            value={selectedOption}
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption?.lead_source_id);
                            }}
                            loadOptions={loadLeadSourceOptions}
                            getOptionLabel={(option) => option?.label || option?.source}
                            getOptionValue={(option) => option?.value || option?.lead_source_id}
                            additional={{
                              pageLimit: 15,
                              page: 0,
                            }}
                            cacheUniqs={[leadSourceSearchQuery, leadSourceList?.length]}
                            debounceTimeout={300}
                            placeholder="Select"
                            className="input-shadow w-100"
                            // changes
                            styles={userContext?.userDetails?.appearance_preference}
                          />
                        );
                      }}
                    />
                    <span className={classNames({ pointer: true })}>
                      <i className="fas fa-plus-circle ms-2" onClick={setSourceToggle} />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {onSourceReferral && (
            <div className="col-md-6">
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-lg-4 col-12 p-medium">Referred From</div>
                <div className="col-lg-8 col-12 d-flex align-items-center gap-2">
                  <div className="flex-1">
                    {referralToggle ? (
                      <ReferredOutDialog onHide={onHideReferral} leadDetails={{ is_lead: true }} onSuccess={referralAddOnSuccess} />
                    ) : (
                      <Controller
                        name="referred_from"
                        control={control}
                        render={({ field }) => {
                          // Find the selected contact from accumulated referredFromOptions or contactList
                          let selectedOption =
                            referredFromOptions?.find((item) => item?.contact_id === field?.value || item?.value === field?.value) || null;
                          if (!selectedOption) {
                            selectedOption =
                              (newlyCreatedReferral?.contact_id === field?.value ? newlyCreatedReferral : null) ||
                              (referredFromId && { label: referredFromValue, value: referredFromId }) ||
                              null;
                          }

                          return (
                            <AsyncPaginate
                              name={field.name}
                              value={selectedOption}
                              onChange={(selectedOption) => {
                                const contactId = selectedOption?.contact_id || selectedOption?.value || null;
                                field.onChange(contactId);
                                if (contactId && selectedOption) {
                                  const { label, value, ...contactData } = selectedOption;
                                  updateContactDetails({
                                    ...contactData,
                                    referral_values: ['INBOUND'],
                                  });
                                }
                              }}
                              loadOptions={loadReferralOptions}
                              getOptionLabel={(option) =>
                                option?.label ||
                                `${option?.title || ''} ${option?.first_name || ''} ${option?.last_name || ''}`.trim() ||
                                option?.business_name ||
                                'Contact'
                              }
                              getOptionValue={(option) => option?.value || option?.contact_id}
                              additional={{
                                pageLimit: 15,
                                pagination_token: '',
                                pagination_direction: 'forward',
                              }}
                              cacheUniqs={[referredFromSearchQuery, contactList?.length]}
                              debounceTimeout={300}
                              placeholder="Select"
                              className="input-shadow w-100"
                              classNamePrefix="async-select-custom"
                              isLoading={contactLoading}
                            />
                          );
                        }}
                      />
                    )}
                  </div>
                  <span className={classNames({ pointer: true })}>
                    <i className="fas fa-plus-circle" onClick={() => setReferralToggle(true)} />
                  </span>
                </div>
              </div>
            </div>
          )}
          {onSourceReferral && (
            <div className="col-md-6">
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-lg-4 col-12 p-medium">Gratitude</div>
                <div className="col-lg-8 col-12">
                  <Controller
                    name="lead_details.gratitude"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        id={field.name}
                        onChange={(e) => field.onChange(e.value)}
                        value={field.value}
                        options={gratitudeOptions}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select"
                        className="input-shadow w-100"
                        filter
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {onSourceReferral && gratitudeValue === 'yes' && (
            <div className="col-md-6">
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-lg-4 col-12 p-medium">Gratitude Item</div>
                <div className="col-lg-8 col-12">
                  <Controller
                    name="lead_details.gratitude_item"
                    control={control}
                    render={({ field }) => (
                      <TextSnippetWrapper type="text" placeholder="Type in" {...field} className="input-shadow w-100" />
                    )}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <>
          <hr />
          <div className="d-flex justify-content-end">
            <Button label="Save Changes" className="p-button p-button-primary" loading={apiLoading} onClick={handleSubmit(updateLead)} />
          </div>
        </>
      </div>
    </React.Fragment>
  );
}
